import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  Badge,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridToolbar,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Build as RevisionIcon,
  Add as AddIcon,
  Person as TechnicianIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  Assignment as AssignIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  PriorityHigh as HighPriorityIcon,
  Warning as MediumPriorityIcon,
  Info as LowPriorityIcon,
  Report as UrgentPriorityIcon,
  Build as MaintenanceIcon,
  Person as ClientRequestIcon,
  CheckCircleOutline as QualityIcon,
  Error as MalfunctionIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useApi, useAsyncOperation } from '../../../hooks/useApi';
import { apiService } from '../../../services/api';
import { Device, User } from '../../../types';
import { formatDate, formatTimeAgo, getDeviceDisplayName } from '../../../utils/helpers';

interface DeviceRevisionsPageProps {}

const DeviceRevisionsPage: React.FC<DeviceRevisionsPageProps> = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState<{
    open: boolean;
    revision?: any;
  }>({ open: false });
  const [completeDialog, setCompleteDialog] = useState<{
    open: boolean;
    revision?: any;
  }>({ open: false });
  const [newRevision, setNewRevision] = useState({
    deviceId: '',
    reason: '',
    priority: 'medium',
    notes: '',
    technicianId: '',
    estimatedCompletionDate: '',
  });
  const [completionNotes, setCompletionNotes] = useState('');
  const [returnToStock, setReturnToStock] = useState(true);

  // API calls
  const {
    data: revisionsData,
    loading: revisionsLoading,
    execute: fetchRevisions,
  } = useApi(() => apiService.getDeviceRevisions({
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    reason: reasonFilter !== 'all' ? reasonFilter : undefined,
    technicianId: selectedTechnician || undefined,
  }));

  const {
    data: technicians,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians());

  const {
    data: availableDevices,
    execute: fetchAvailableDevices,
  } = useApi(() => apiService.getDevices({ status: 'inactive' }));

  const { execute: createRevision, loading: creatingRevision } = useAsyncOperation();
  const { execute: assignTechnician, loading: assigningTechnician } = useAsyncOperation();
  const { execute: completeRevision, loading: completingRevision } = useAsyncOperation();
  const { execute: cancelRevision } = useAsyncOperation();

  useEffect(() => {
    fetchRevisions();
    fetchTechnicians();
    fetchAvailableDevices();
  }, [priorityFilter, reasonFilter, selectedTechnician]);

  const handleCreateRevision = async () => {
    if (!newRevision.deviceId || !newRevision.reason) {
      return;
    }

    const result = await createRevision(() => apiService.createDeviceRevision({
      deviceId: newRevision.deviceId,
      reason: newRevision.reason as any,
      priority: newRevision.priority as any,
      notes: newRevision.notes,
      technicianId: newRevision.technicianId || undefined,
      estimatedCompletionDate: newRevision.estimatedCompletionDate || undefined,
    }));

    if (result) {
      setCreateDialog(false);
      setNewRevision({
        deviceId: '',
        reason: '',
        priority: 'medium',
        notes: '',
        technicianId: '',
        estimatedCompletionDate: '',
      });
      fetchRevisions();
    }
  };

  const handleAssignTechnician = async () => {
    if (!assignDialog.revision || !selectedTechnician) return;

    const result = await assignTechnician(() =>
      apiService.assignRevisionTechnician(assignDialog.revision.device._id, selectedTechnician)
    );

    if (result) {
      setAssignDialog({ open: false });
      setSelectedTechnician('');
      fetchRevisions();
    }
  };

  const handleCompleteRevision = async () => {
    if (!completeDialog.revision) return;

    const result = await completeRevision(() =>
      apiService.completeDeviceRevision(completeDialog.revision.device._id, {
        completionNotes,
        returnToStock,
      })
    );

    if (result) {
      setCompleteDialog({ open: false });
      setCompletionNotes('');
      setReturnToStock(true);
      fetchRevisions();
    }
  };

  const handleCancelRevision = async (revision: any) => {
    const reason = prompt('Ingresa la razón de cancelación:');
    if (!reason) return;

    const result = await cancelRevision(() =>
      apiService.cancelDeviceRevision(revision.device._id, reason)
    );

    if (result) {
      fetchRevisions();
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <UrgentPriorityIcon sx={{ color: 'error.main' }} />;
      case 'high': return <HighPriorityIcon sx={{ color: 'warning.main' }} />;
      case 'medium': return <MediumPriorityIcon sx={{ color: 'info.main' }} />;
      case 'low': return <LowPriorityIcon sx={{ color: 'success.main' }} />;
      default: return <MediumPriorityIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getReasonIcon = (reason: string) => {
    switch (reason) {
      case 'maintenance': return <MaintenanceIcon />;
      case 'client_request': return <ClientRequestIcon />;
      case 'quality_check': return <QualityIcon />;
      case 'malfunction': return <MalfunctionIcon />;
      default: return <RevisionIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'maintenance': return 'Mantenimiento';
      case 'client_request': return 'Solicitud Cliente';
      case 'quality_check': return 'Control Calidad';
      case 'malfunction': return 'Mal Funcionamiento';
      default: return reason;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return priority;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'device',
      headerName: 'Dispositivo',
      width: 200,
      renderCell: (params: any) => {
        const device = params.value;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>
              <RevisionIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getDeviceDisplayName(device)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                IMEI: {device.imei}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'revisionInfo',
      headerName: 'Revisión',
      width: 180,
      renderCell: (params: any) => {
        const info = params.value;
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {getReasonIcon(info.revisionReason)}
              <Typography variant="body2">
                {getReasonLabel(info.revisionReason)}
              </Typography>
            </Box>
            <Chip
              icon={getPriorityIcon(info.priority)}
              label={getPriorityLabel(info.priority)}
              size="small"
              color={getPriorityColor(info.priority) as any}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'assignedTechnician',
      headerName: 'Técnico',
      width: 150,
      renderCell: (params: any) => {
        const technician = params.row.revisionInfo?.assignedTechnicianId;
        return technician ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'info.main' }}>
              <TechnicianIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2">
              {technician.firstName && technician.lastName ? `${technician.firstName} ${technician.lastName}` : technician.name}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin asignar
          </Typography>
        );
      },
    },
    {
      field: 'revisionStartDate',
      headerName: 'Fecha Inicio',
      width: 120,
      renderCell: (params: any) => (
        <Typography variant="caption">
          {params.row.revisionInfo?.revisionStartDate ?
            formatDate(params.row.revisionInfo.revisionStartDate) : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'estimatedCompletion',
      headerName: 'Est. Finalización',
      width: 140,
      renderCell: (params: any) => (
        <Typography variant="caption">
          {params.row.revisionInfo?.estimatedCompletionDate ?
            formatDate(params.row.revisionInfo.estimatedCompletionDate) : 'Sin estimar'}
        </Typography>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notas',
      width: 200,
      renderCell: (params: any) => (
        <Tooltip title={params.row.revisionInfo?.revisionNotes || 'Sin notas'}>
          <Typography variant="body2" sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.row.revisionInfo?.revisionNotes || 'Sin notas'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 180,
      getActions: (params: GridRowParams) => [
        ...(params.row.revisionInfo?.assignedTechnicianId ? [] : [
          <GridActionsCellItem
            icon={<AssignIcon />}
            label="Asignar Técnico"
            onClick={() => setAssignDialog({ open: true, revision: params.row })}
          />
        ]),
        <GridActionsCellItem
          icon={<CompleteIcon />}
          label="Completar"
          onClick={() => setCompleteDialog({ open: true, revision: params.row })}
        />,
        <GridActionsCellItem
          icon={<CancelIcon />}
          label="Cancelar"
          onClick={() => handleCancelRevision(params.row)}
        />,
        <GridActionsCellItem
          icon={<HistoryIcon />}
          label="Historial"
          onClick={() => navigate(`/devices/${params.row.device._id}/revision-history`)}
        />,
      ],
    },
  ];

  const revisionsStats = revisionsData?.stats || { total: 0, byPriority: {}, byReason: {} };
  const revisions = revisionsData?.devices || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Revisiones de Dispositivos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona las revisiones y mantenimiento de dispositivos GPS
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchRevisions()}
            disabled={revisionsLoading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
            size="large"
          >
            Nueva Revisión
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {revisionsStats.total}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Total Revisiones
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" sx={{ fontWeight: 600 }}>
                {revisionsStats.byPriority?.urgent || 0}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Urgentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {revisionsStats.byPriority?.high || 0}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Alta Prioridad
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {(revisionsStats.byReason?.maintenance || 0) + (revisionsStats.byReason?.quality_check || 0)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Mantenimiento
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por IMEI o notas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Prioridad</InputLabel>
            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              label="Prioridad"
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="urgent">Urgente</MenuItem>
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="medium">Media</MenuItem>
              <MenuItem value="low">Baja</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Razón</InputLabel>
            <Select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              label="Razón"
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="maintenance">Mantenimiento</MenuItem>
              <MenuItem value="client_request">Solicitud Cliente</MenuItem>
              <MenuItem value="quality_check">Control Calidad</MenuItem>
              <MenuItem value="malfunction">Mal Funcionamiento</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Técnico</InputLabel>
            <Select
              value={selectedTechnician}
              onChange={(e) => setSelectedTechnician(e.target.value)}
              label="Técnico"
            >
              <MenuItem value="">Todos</MenuItem>
              {(technicians as any)?.data?.map((tech: User) => (
                <MenuItem key={tech._id} value={tech._id}>
                  {tech.firstName && tech.lastName ? `${tech.firstName} ${tech.lastName}` : tech.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Revisions Table */}
      <Paper>
        <DataGrid
          rows={revisions}
          columns={columns}
          loading={revisionsLoading}
          pagination
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          getRowId={(row) => row.device._id}
          slots={{
            toolbar: GridToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            border: 0,
            minHeight: 400,
          }}
        />
      </Paper>

      {/* Create Revision Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Crear Nueva Revisión</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Dispositivo</InputLabel>
              <Select
                value={newRevision.deviceId}
                label="Dispositivo"
                onChange={(e) => setNewRevision({ ...newRevision, deviceId: e.target.value })}
              >
                {(availableDevices as any)?.data?.map((device: Device) => (
                  <MenuItem key={device._id} value={device._id}>
                    {getDeviceDisplayName(device)} - IMEI: {device.imei}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Razón</InputLabel>
              <Select
                value={newRevision.reason}
                label="Razón"
                onChange={(e) => setNewRevision({ ...newRevision, reason: e.target.value })}
              >
                <MenuItem value="maintenance">Mantenimiento</MenuItem>
                <MenuItem value="client_request">Solicitud del Cliente</MenuItem>
                <MenuItem value="quality_check">Control de Calidad</MenuItem>
                <MenuItem value="malfunction">Mal Funcionamiento</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Prioridad</InputLabel>
              <Select
                value={newRevision.priority}
                label="Prioridad"
                onChange={(e) => setNewRevision({ ...newRevision, priority: e.target.value })}
              >
                <MenuItem value="low">Baja</MenuItem>
                <MenuItem value="medium">Media</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Técnico (Opcional)</InputLabel>
              <Select
                value={newRevision.technicianId}
                label="Técnico (Opcional)"
                onChange={(e) => setNewRevision({ ...newRevision, technicianId: e.target.value })}
              >
                <MenuItem value="">Sin asignar</MenuItem>
                {(technicians as any)?.data?.map((tech: User) => (
                  <MenuItem key={tech._id} value={tech._id}>
                    {tech.firstName && tech.lastName ? `${tech.firstName} ${tech.lastName}` : tech.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Fecha Estimada de Finalización (Opcional)"
              type="date"
              value={newRevision.estimatedCompletionDate}
              onChange={(e) => setNewRevision({ ...newRevision, estimatedCompletionDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Notas"
              multiline
              rows={3}
              value={newRevision.notes}
              onChange={(e) => setNewRevision({ ...newRevision, notes: e.target.value })}
              placeholder="Descripción del problema o detalles de la revisión..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRevision}
            disabled={!newRevision.deviceId || !newRevision.reason || creatingRevision}
          >
            Crear Revisión
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Technician Dialog */}
      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Asignar Técnico</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {assignDialog.revision && (
              <Alert severity="info">
                Asignando técnico para dispositivo: <strong>{assignDialog.revision.device.name || assignDialog.revision.device.imei}</strong>
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Técnico</InputLabel>
              <Select
                value={selectedTechnician}
                label="Técnico"
                onChange={(e) => setSelectedTechnician(e.target.value)}
              >
                {(technicians as any)?.data?.map((tech: User) => (
                  <MenuItem key={tech._id} value={tech._id}>
                    {tech.firstName && tech.lastName ? `${tech.firstName} ${tech.lastName}` : tech.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignTechnician}
            disabled={!selectedTechnician || assigningTechnician}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Revision Dialog */}
      <Dialog
        open={completeDialog.open}
        onClose={() => setCompleteDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Completar Revisión</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {completeDialog.revision && (
              <Alert severity="success">
                Completando revisión para dispositivo: <strong>{completeDialog.revision.device.name || completeDialog.revision.device.imei}</strong>
              </Alert>
            )}
            <TextField
              fullWidth
              label="Notas de Finalización"
              multiline
              rows={3}
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="Descripción del trabajo realizado..."
            />
            <FormControl fullWidth>
              <InputLabel>Acción después de completar</InputLabel>
              <Select
                value={returnToStock ? 'stock' : 'keep'}
                label="Acción después de completar"
                onChange={(e) => setReturnToStock(e.target.value === 'stock')}
              >
                <MenuItem value="stock">Devolver a Stock</MenuItem>
                <MenuItem value="keep">Mantener estado actual</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog({ open: false })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteRevision}
            disabled={completingRevision}
          >
            Completar Revisión
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceRevisionsPage;